import React, { useEffect, useState } from 'react';
import { Loader2, AlertCircle, RefreshCw, Folder, File, Archive } from 'lucide-react';
import JSZip from 'jszip';
import { formatFileSize } from './attachmentUtils';

interface ArchivePreviewProps {
  url: string;
  fileName: string;
}

interface ZipEntryInfo {
  name: string;
  isDir: boolean;
  size: number;
}

export const ArchivePreview: React.FC<ArchivePreviewProps> = ({ url, fileName }) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isError, setIsError] = useState<boolean>(false);
  const [entries, setEntries] = useState<ZipEntryInfo[]>([]);
  const [totalFiles, setTotalFiles] = useState<number>(0);
  const [totalSize, setTotalSize] = useState<number>(0);

  const loadArchive = async () => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const arrayBuffer = await response.arrayBuffer();
      const zip = await JSZip.loadAsync(arrayBuffer);

      const parsed: ZipEntryInfo[] = [];
      let fileCount = 0;
      let uncompressedBytes = 0;

      zip.forEach((relativePath, zipEntry) => {
        parsed.push({
          name: relativePath,
          isDir: zipEntry.dir,
          size: (zipEntry as any)._data?.uncompressedSize || 0,
        });
        if (!zipEntry.dir) {
          fileCount++;
          uncompressedBytes += (zipEntry as any)._data?.uncompressedSize || 0;
        }
      });

      // Sort directories first, then files
      parsed.sort((a, b) => {
        if (a.isDir && !b.isDir) return -1;
        if (!a.isDir && b.isDir) return 1;
        return a.name.localeCompare(b.name);
      });

      setEntries(parsed.slice(0, 2000));
      setTotalFiles(fileCount);
      setTotalSize(uncompressedBytes);
    } catch (err) {
      console.warn('[ArchivePreview] Failed to parse ZIP archive:', err);
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadArchive();
  }, [url]);

  if (isLoading) {
    return (
      <div className="flex-1 w-full h-full bg-slate-950 flex flex-col items-center justify-center gap-3 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <p className="text-xs font-semibold">Reading ZIP archive headers ({fileName})...</p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto gap-3">
        <AlertCircle className="h-10 w-10 text-amber-400" />
        <p className="font-bold text-foreground text-sm">Archive Inspection Failed</p>
        <p className="text-xs text-muted-foreground">Unable to parse ZIP header contents in browser.</p>
        <button
          type="button"
          onClick={loadArchive}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-muted hover:bg-muted/80 text-foreground font-semibold text-xs transition mt-2"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          <span>Retry</span>
        </button>
      </div>
    );
  }

  return (
    <div className="flex-1 w-full h-full bg-slate-950 flex flex-col overflow-hidden text-slate-200 select-text">
      {/* Archive Header Banner */}
      <div className="p-3 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-6 text-xs text-slate-400">
        <div className="flex items-center gap-2">
          <Archive className="h-4 w-4 text-purple-400" />
          <span className="font-bold text-slate-200">{fileName}</span>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <span>Files: <strong className="text-slate-200">{totalFiles}</strong></span>
          <span>Uncompressed Size: <strong className="text-slate-200">{formatFileSize(totalSize)}</strong></span>
        </div>
      </div>

      {/* Directory Tree */}
      <div className="flex-1 overflow-auto p-4 font-mono text-xs space-y-1">
        {entries.map((entry, idx) => (
          <div
            key={idx}
            className="flex items-center justify-between p-2 rounded-lg hover:bg-slate-900/60 transition border border-transparent hover:border-slate-800/80"
          >
            <div className="flex items-center gap-2.5 min-w-0">
              {entry.isDir ? (
                <Folder className="h-4 w-4 text-amber-400 flex-shrink-0" />
              ) : (
                <File className="h-4 w-4 text-slate-400 flex-shrink-0" />
              )}
              <span className={`truncate ${entry.isDir ? 'font-bold text-slate-200' : 'text-slate-300'}`}>
                {entry.name}
              </span>
            </div>

            {!entry.isDir && (
              <span className="text-[11px] text-slate-500 font-sans flex-shrink-0 ml-4">
                {formatFileSize(entry.size)}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
